import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import GoldRatesWidget from "@/components/GoldRatesWidget";
import ReelsWall from "@/components/ReelsWall";
import { ProductSkeleton } from "@/components/Skeletons";
import { useSettings } from "@/context/SettingsContext";
import { ShieldCheck, Sparkles, Gem, HandHeart, Feather, Award, Layers, Palette, ArrowRight, Star } from "lucide-react";

const CATEGORIES = [
    { name: "Plain Gold", path: "/gold-jewellery", image: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=800", tag: "Timeless" },
    { name: "Silver", path: "/silver-jewellery", image: "https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=800", tag: "Pure 925" },
    { name: "Diamond", path: "/diamond-jewellery", image: "https://images.unsplash.com/photo-1607703829739-c05b7beddf60?w=800", tag: "Radiance" },
    { name: "Traditional Bridal", path: "/bridal-jewellery", image: "https://images.unsplash.com/photo-1617633150878-7df1d12a9a57?w=800", tag: "Heirloom" },
    { name: "Everyday", path: "/products?collection=Everyday", image: "https://images.unsplash.com/photo-1602752275197-9e0f6a3d3f70?w=800", tag: "Effortless" },
];

const WHY = [
    { icon: Award, title: "Exceptional Quality", text: "Craftsmanship rooted in tradition, finished with precision." },
    { icon: HandHeart, title: "Competitive Making Charges", text: "Honest pricing that respects every rupee you invest." },
    { icon: ShieldCheck, title: "Purity & Transparency", text: "Every piece is presented with clear purity and weight." },
    { icon: Sparkles, title: "Elegant Designs", text: "Curated for celebrations, traditions and daily elegance." },
    { icon: Feather, title: "Personalised Service", text: "Considered guidance from our jewellery consultants." },
    { icon: Gem, title: "Wide Collection", text: "Gold, silver, diamond and bridal — all under one roof." },
    { icon: Layers, title: "Trusted Expertise", text: "Serving families across Kotdwar with quiet confidence." },
    { icon: Palette, title: "Custom Jewellery", text: "Bring us your idea. We craft it into a keepsake." },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [newArr, setNewArr] = useState([]);
    const [traditional, setTraditional] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const { settings } = useSettings();
    const [slideIdx, setSlideIdx] = useState(0);

    const slides = settings.hero_slides || [
        { image: "/hero/bride-hero.jpeg", branded: true },
        { image: "https://images.unsplash.com/photo-1770747873474-fd681fb8d53f?w=1800", title: "THE TRADITIONAL BRIDE", subtitle: "Sharp features. Complete bridal makeover. Adorned in pure plain gold." },
        { image: "https://images.unsplash.com/photo-1762709414326-67c887a8dc98?w=1800", title: "PLAIN GOLD, PURE HERITAGE", subtitle: "Chandrahaar, Guluband, Hansuli — crafted the way our mothers wore them." },
    ];

    useEffect(() => {
        client.get("/products?featured=true&limit=8").then(r => setFeatured(r.data));
        client.get("/products?new_arrival=true&limit=8").then(r => setNewArr(r.data));
        client.get("/products?collection=Traditional%20Heritage&limit=8").then(r => setTraditional(r.data));
        client.get("/testimonials").then(r => setTestimonials(r.data));
    }, []);

    useEffect(() => {
        const t = setInterval(() => setSlideIdx(i => (i + 1) % slides.length), 6000);
        return () => clearInterval(t);
    }, [slides.length]);

    const slide = slides[slideIdx] || slides[0];

    return (
        <div>
            {/* HERO */}
            <section className="relative w-full aspect-[4/5] md:aspect-[16/9] max-h-[92vh] overflow-hidden bg-[#3a0d0d]" data-testid="hero">
                {slides.map((s, i) => (
                    <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === slideIdx ? "opacity-100" : "opacity-0"}`}>
                        <img
                            src={s.image}
                            alt={s.title || "Singh Jewellers"}
                            className={`w-full h-full ${s.branded ? "object-contain md:object-cover object-center" : "object-cover object-[center_top]"}`}
                        />
                        {!s.branded && <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--charcoal)]/20 via-transparent to-[color:var(--charcoal)]/70" />}
                    </div>
                ))}
                {!slide.branded ? (
                    <div className="relative z-10 h-full flex items-end pb-20 lg:pb-32">
                        <div className="max-w-[1440px] w-full mx-auto px-6 lg:px-10">
                            <div className="max-w-2xl fade-in">
                                <div className="eyebrow text-[color:var(--champagne-light)] mb-4">Kotdwar · Since Generations</div>
                                <h1 className="font-serif-luxe text-white text-5xl md:text-7xl lg:text-8xl leading-[0.95] mb-6" data-testid="hero-title">
                                    {slide.title}
                                </h1>
                                <p className="font-serif-luxe italic text-white/85 text-xl md:text-2xl mb-8 max-w-lg">
                                    {slide.subtitle}
                                </p>
                                <p className="text-white/70 mb-8 max-w-md text-sm md:text-base leading-relaxed">
                                    From heirloom Chandrahaars and Gulubands to everyday plain gold — jewellery crafted for celebrations, traditions and everyday elegance.
                                </p>
                                <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 max-w-md md:max-w-none">
                                    <Link to="/products" className="btn-gold text-[10px] md:text-xs" data-testid="hero-explore">Explore Collection</Link>
                                    <Link to="/bridal-jewellery" className="btn-outline text-white border-white hover:bg-white hover:text-[color:var(--charcoal)] text-[10px] md:text-xs">Traditional Bridal</Link>
                                    <Link to="/contact" className="col-span-2 md:col-auto btn-outline text-white border-white/50 hover:bg-white hover:text-[color:var(--charcoal)] text-[10px] md:text-xs">Book Showroom Visit</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-10 flex gap-2">
                    {slides.map((_, i) => (
                        <button key={i} onClick={() => setSlideIdx(i)} className={`h-[2px] transition-all ${i === slideIdx ? "w-10 bg-[color:var(--champagne)]" : "w-5 bg-white/50"}`} data-testid={`hero-dot-${i}`} aria-label={`Slide ${i+1}`} />
                    ))}
                </div>
            </section>

            {/* Hero CTA strip — always visible under hero */}
            <section className="bg-[color:var(--charcoal)] text-[color:var(--ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-4 md:py-5 flex flex-wrap items-center justify-center gap-3 md:gap-4">
                    <Link to="/bridal-jewellery" className="btn-gold text-[10px] md:text-xs" data-testid="hero-cta-bridal">Traditional Bridal</Link>
                    <Link to="/products" className="btn-outline text-white border-white/50 hover:bg-white hover:text-[color:var(--charcoal)] text-[10px] md:text-xs" data-testid="hero-cta-shop">Explore Collection</Link>
                    <Link to="/contact" className="btn-outline text-white border-white/50 hover:bg-white hover:text-[color:var(--charcoal)] text-[10px] md:text-xs" data-testid="hero-cta-visit">Book Showroom Visit</Link>
                </div>
            </section>

            {/* Editorial Bride Story — Magazine Spread */}
            <section className="py-20 md:py-24" data-testid="bride-editorial">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                    <div className="text-center mb-12">
                        <div className="eyebrow">The Bridal Story</div>
                        <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-tight">Timeless traditions,<br /><span className="italic text-[color:var(--champagne)]">golden forever.</span></h2>
                        <div className="w-16 h-[1px] bg-[color:var(--champagne)] mx-auto mt-6" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                        <div className="group relative overflow-hidden card-luxe">
                            <img src="/hero/bride-collage-1.jpeg" alt="Traditional Bride Story" className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]" draggable="false" />
                        </div>
                        <div className="group relative overflow-hidden card-luxe">
                            <img src="/hero/bride-collage-2.jpeg" alt="Traditions Golden Forever" className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]" draggable="false" />
                        </div>
                    </div>
                    <div className="text-center mt-10">
                        <Link to="/bridal-jewellery" className="btn-primary">Explore Bridal Heritage</Link>
                    </div>
                </div>
            </section>

            {/* Live Rates */}
            <section className="py-14 md:py-16 bg-[color:var(--beige)]/40">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-wrap items-end justify-between mb-8 gap-4">
                        <div>
                            <div className="eyebrow">Today's Market Rates</div>
                            <h2 className="font-serif-luxe text-3xl md:text-4xl text-[color:var(--charcoal)] mt-2">Gold & Silver Prices</h2>
                            <p className="text-xs text-[color:var(--muted-brown)] mt-2">Updated regularly · Confirm today's final rate at the showroom</p>
                        </div>
                        <Link to="/gold-rates" className="text-sm text-[color:var(--charcoal)] link-underline">View full rate details →</Link>
                    </div>
                    <GoldRatesWidget />
                    <p className="text-xs text-[color:var(--muted-brown)] mt-6 text-center max-w-2xl mx-auto">Rates are indicative and subject to change. Please confirm today's final rate with Singh Jewellers. Follow our WhatsApp channel for live rate updates.</p>
                </div>
            </section>

            {/* Categories */}
            <section className="py-20 md:py-28">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                    <div className="text-center mb-14">
                        <div className="eyebrow">Discover</div>
                        <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Shop by Jewellery</h2>
                        <div className="w-16 h-[1px] bg-[color:var(--champagne)] mx-auto mt-5" />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                        {CATEGORIES.map((c, i) => (
                            <Link key={c.name} to={c.path} className="group relative overflow-hidden aspect-[3/4] card-luxe" data-testid={`cat-${c.name.toLowerCase()}`}>
                                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--charcoal)]/85 via-[color:var(--charcoal)]/10 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-5">
                                    <div className="eyebrow text-[color:var(--champagne-light)] mb-1">{c.tag}</div>
                                    <div className="font-serif-luxe text-white text-2xl">{c.name}</div>
                                    <div className="text-[11px] tracking-[0.22em] text-white/70 mt-2 flex items-center gap-1 group-hover:text-[color:var(--champagne-light)]">EXPLORE <ArrowRight size={12} /></div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Promo Cards */}
            <section className="py-8">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-4">
                    {[
                        { title: "Today's Gold Rate", desc: "Live 24K, 22K, 18K & silver prices.", to: "/gold-rates", cta: "Check Rate" },
                        { title: "Gold Harvest Plan", desc: "Save monthly. Redeem in exquisite jewellery.", to: "/gold-harvest", cta: "Learn More" },
                        { title: "Kitty Plan", desc: "Save together with family and friends.", to: "/kitty-plan", cta: "Discover" },
                    ].map((p) => (
                        <Link key={p.title} to={p.to} className="card-luxe p-8 group" data-testid={`promo-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                            <div className="eyebrow mb-3">SINGH JEWELLERS</div>
                            <h3 className="font-serif-luxe text-2xl mb-2">{p.title}</h3>
                            <p className="text-sm text-[color:var(--muted-brown)] mb-5">{p.desc}</p>
                            <span className="text-[11px] tracking-[0.22em] font-semibold text-[color:var(--charcoal)] group-hover:text-[color:var(--champagne)]">{p.cta} →</span>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Featured */}
            {featured.length > 0 ? (
                <section className="py-20 md:py-24">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
                            <div>
                                <div className="eyebrow">Curated for You</div>
                                <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Featured Jewellery</h2>
                            </div>
                            <Link to="/products?featured=true" className="text-sm link-underline">View all →</Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
                            {featured.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                </section>
            ) : (
                <section className="py-20 md:py-24">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                        <div className="mb-10"><div className="eyebrow">Curated for You</div><h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Featured Jewellery</h2></div>
                        <ProductSkeleton count={8} />
                    </div>
                </section>
            )}

            {/* Why Choose */}
            <section className="py-24 bg-[color:var(--warm-black)] text-[color:var(--ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                    <div className="text-center mb-14">
                        <div className="eyebrow text-[color:var(--champagne)]">Why Choose Us</div>
                        <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">Jewellery You Can Trust</h2>
                        <p className="mt-4 text-white/70 max-w-xl mx-auto text-sm">Because jewellery is not just an ornament. It is a symbol of trust, memories and generations.</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                        {WHY.map(({ icon: Icon, title, text }) => (
                            <div key={title} className="border border-white/10 p-6 hover:border-[color:var(--champagne)] transition-colors">
                                <Icon size={24} className="text-[color:var(--champagne)] mb-4" />
                                <div className="font-serif-luxe text-lg mb-2">{title}</div>
                                <div className="text-xs text-white/60 leading-relaxed">{text}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Traditional Heritage — Featured Collection */}
            <section className="py-24 bg-[color:var(--beige)]/50">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-4">
                        <div className="max-w-xl">
                            <div className="eyebrow">Heritage · Since Generations</div>
                            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3 leading-tight">The Traditional Heritage Collection</h2>
                            <p className="text-[color:var(--muted-brown)] mt-5 leading-relaxed">
                                Tehri Nath, Bulakh, Guluband, Chandrahaar, Hansuli, Mundvali, Murki and Ponchi. Sacred forms passed hand-to-hand through generations — crafted in plain, honest gold, the way our mothers and grandmothers wore them.
                            </p>
                        </div>
                        <Link to="/products?collection=Traditional%20Heritage" className="btn-primary self-start">Explore Heritage</Link>
                    </div>

                    {/* Reference pieces card */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
                        {[
                            {name: "Tehri Nath", img: "https://images.unsplash.com/photo-1610694955371-d4a3e0ce4b52?w=600", desc: "The heirloom nose ring"},
                            {name: "Bulakh", img: "https://images.unsplash.com/photo-1600685890506-593fdf55949b?w=600", desc: "The septum ornament"},
                            {name: "Guluband", img: "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?w=600", desc: "The heritage choker"},
                            {name: "Ponchi", img: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=600", desc: "Traditional bangles"},
                            {name: "Murki", img: "https://images.unsplash.com/photo-1630019852942-f89202989a59?w=600", desc: "Delicate ear studs"},
                        ].map(p => (
                            <div key={p.name} className="group card-luxe overflow-hidden" data-testid={`ref-${p.name.toLowerCase()}`}>
                                <div className="relative aspect-square product-img-wrap" onContextMenu={(e) => e.preventDefault()}>
                                    <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" draggable="false" />
                                    <img className="watermark" src="/logo-watermark.png" alt="" />
                                </div>
                                <div className="p-3 text-center">
                                    <div className="font-serif-luxe text-base">{p.name}</div>
                                    <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted-brown)] mt-1">{p.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {traditional.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
                            {traditional.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    ) : null}
                </div>
            </section>

            {/* Bridal Editorial */}
            <section className="py-24">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
                    <div className="relative aspect-[4/5] product-img-wrap">
                        <img src="https://images.unsplash.com/photo-1770747873474-fd681fb8d53f?w=1200" alt="Traditional Bride" className="w-full h-full object-cover" />
                        <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                    </div>
                    <div className="lg:pl-10">
                        <div className="eyebrow">The Traditional Bride</div>
                        <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">A wedding rooted in tradition, told in gold.</h2>
                        <p className="text-[color:var(--muted-brown)] mt-6 leading-relaxed">
                            Every traditional bride carries a story — the Tehri Nath resting proud on her cheek, the Guluband hugging her throat, the Chandrahaar cascading like moonlight. We craft each piece in plain, high-purity gold to honour the traditions that shaped us.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/bridal-jewellery" className="btn-primary">Explore Bridal</Link>
                            <Link to="/contact" className="btn-outline">Book Bridal Consultation</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* New Arrivals */}
            {newArr.length > 0 && (
                <section className="py-20">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
                            <div>
                                <div className="eyebrow">Fresh In</div>
                                <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">New Arrivals</h2>
                            </div>
                            <Link to="/new-arrivals" className="text-sm link-underline">View all →</Link>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7">
                            {newArr.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* Testimonials */}
            {testimonials.length > 0 && (
                <section className="py-24 bg-[color:var(--beige)]/40">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
                        <div className="text-center mb-14">
                            <div className="eyebrow">Family Words</div>
                            <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">In Their Words</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {testimonials.slice(0, 3).map(t => (
                                <div key={t.id} className="card-luxe p-8">
                                    <div className="flex gap-1 text-[color:var(--champagne)] mb-4">
                                        {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                                    </div>
                                    <p className="font-serif-luxe italic text-lg text-[color:var(--charcoal)] leading-relaxed mb-6">"{t.review}"</p>
                                    <div className="eyebrow">{t.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Reels Wall */}
            <ReelsWall />

            {/* Showroom */}
            <section className="py-24 bg-[color:var(--charcoal)] text-[color:var(--ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="eyebrow text-[color:var(--champagne)]">Visit Us</div>
                        <h2 className="font-serif-luxe text-4xl md:text-5xl mt-3">The Singh Jewellers Showroom</h2>
                        <p className="mt-6 text-white/70 leading-relaxed max-w-md">Come home to jewellery. Experience our collection, talk with our consultants, and try on pieces at your leisure.</p>
                        <div className="mt-8 space-y-2 text-sm text-white/85">
                            <div>{settings.address}</div>
                            <div>{settings.hours}</div>
                            <div className="text-[color:var(--champagne-light)]">{settings.phone}</div>
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Link to="/contact" className="btn-gold">Book a Visit</Link>
                            <a href={settings.maps_url || "#"} target="_blank" rel="noreferrer" className="btn-outline text-white border-white/60 hover:bg-white hover:text-[color:var(--charcoal)]">Get Directions</a>
                        </div>
                    </div>
                    <div className="relative aspect-video product-img-wrap">
                        <img src="https://images.unsplash.com/photo-1600685890506-593fdf55949b?w=1200" alt="Showroom" className="w-full h-full object-cover" />
                        <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                    </div>
                </div>
            </section>
        </div>
    );
}
